
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { getUdaUrl, usePortal } from '../../pages/home/PortalContext';
import { getUdaThemeBySlug } from '../../pages/home/udaThemes';
import { SectionHeading } from '../../pages/home/components/SectionHeading';
import { TILE_URLS } from '@/config/mapTiles';

interface UDA {
  id: number;       // matches the feature id in ap-udas.geojson
  slug: string;     // UDA code slug (also subdomain label)
  name: string;     // short display name
  district: string;
  lat: number;
  lng: number;
}

/* Which UDAs exist and what they are called comes from ds_uda_lst_t; the table
   carries no coordinates, so each row is joined by uda_cd to its feature in
   ap-udas.geojson (itself generated from the maud_gis boundary tables) for the
   id/district/lat/lng. Nothing about a UDA is hardcoded here. A row with no
   matching feature cannot be placed on the map and is skipped — re-run
   server/generate-uda-geojson.js to add it. */

// A few newer UDA master rows were added before their GeoJSON boundary was
// generated. These centres keep those authorities visible as point markers
// until their polygon is published.
const UDA_CENTER_FALLBACKS: Record<string, [number, number]> = {
  bhuda: [16.5449, 81.5212],
  mruda: [15.7350, 79.2684],
  nduda: [15.4786, 78.4875],
};

const buildUdaList = (
  rows: Array<{ uda_id?: number | string; uda_cd?: string; uda_nm?: string; cntr_lat?: number | string; cntr_lng?: number | string }>,
  features: Array<{ properties?: any }>,
): UDA[] =>
  rows.reduce<UDA[]>((acc, row) => {
    const slug = (row.uda_cd || '').toLowerCase();
    const p = features.find((f) => f.properties?.slug === slug)?.properties;
    const fallback = UDA_CENTER_FALLBACKS[slug];
    const lat = Number(p?.lat ?? row.cntr_lat ?? fallback?.[0]);
    const lng = Number(p?.lng ?? row.cntr_lng ?? fallback?.[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      console.warn(`[UDA list] no map coordinates for code "${row.uda_cd}" — shown without map position`);
    }
    acc.push({
      id: p?.id ?? Number(row.uda_id),
      slug,
      // THE UDA name (ds_uda_lst_t.uda_nm), shown as it is — no local trimming or suffix.
      name: row.uda_nm || p?.name || slug,
      district: p?.district || 'Andhra Pradesh',
      lat: Number.isFinite(lat) ? lat : 0,
      lng: Number.isFinite(lng) ? lng : 0,
    });
    return acc;
  }, []);

const ACCENT = '#E66A1A';
const ACCENT_DARK = '#B8480E';

// Soft, harmonious pastel palette used to tint each district a distinct colour
// (categorical choropleth). Kept low-saturation so the UDA accent still pops.
const DISTRICT_PALETTE = [
  '#A8DADC', '#FFE3A3', '#C9E4CA', '#F4C2C2', '#BFD7EA',
  '#E2C2F4', '#FFD6A5', '#C7CEEA', '#B5EAD7', '#FFDAC1',
  '#D9E8C8', '#F7D6E0',
];

export const ModulesSection: React.FC = () => {
  const { t } = useTranslation();
  const { publicApi } = usePortal();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const hoveredRef = useRef<number | null>(null);
  const udaPropsRef = useRef<Map<number, any>>(new Map());
  const listScrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLAnchorElement>>(new Map());
  // udaId -> resolved photo URL (real uploaded image, themed hero as fallback)
  const imageCacheRef = useRef<Map<number, string>>(new Map());
  const showPopupRef = useRef<(id: number) => void>(() => {});
  const [mapLoaded, setMapLoaded] = useState(false);
  const [activeUdaId, setActiveUdaId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [udaList, setUdaList] = useState<UDA[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      publicApi.get('/udas').then((res) => res.data?.data || []),
      fetch('/geo/ap-udas.geojson').then((r) => r.json()).then((fc) => fc.features || []),
    ])
      .then(([rows, features]) => {
        if (cancelled || !Array.isArray(rows) || !Array.isArray(features)) return;
        setUdaList(buildUdaList(rows, features));
      })
      .catch((e) => console.warn('[UDA list] could not load UDAs:', e?.message));
    return () => { cancelled = true; };
  }, [publicApi]);
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation();

  const buildUdaUrl = useCallback((uda: { slug: string }) => getUdaUrl(uda.slug), []);

  // ── Apply / clear the hover highlight on the map (drives feature-state) ──
  const setMapHover = useCallback((id: number | null) => {
    const m = map.current;
    if (!m || !m.isStyleLoaded()) return;
    const prev = hoveredRef.current;
    if (prev != null && prev !== id) {
      m.setFeatureState({ source: 'udas', id: prev }, { hover: false });
      m.setFeatureState({ source: 'uda-dots', id: prev }, { hover: false });
    }
    hoveredRef.current = id;
    if (id != null) {
      m.setFeatureState({ source: 'udas', id }, { hover: true });
      m.setFeatureState({ source: 'uda-dots', id }, { hover: true });
    }
  }, []);

  // ── Show the data popup for a UDA at its centre ──
  const showPopup = useCallback((id: number) => {
    const m = map.current;
    const p = udaPropsRef.current.get(id);
    if (!m || !p) return;

    // Resolve a real photo for this UDA. Seed with the themed hero immediately,
    // then lazily pull the actual uploaded carousel image (same source the UDA
    // landing page uses) and re-render the popup once it arrives.
    if (!imageCacheRef.current.has(id)) {
      const theme = getUdaThemeBySlug(p.slug);
      if (theme) {
        imageCacheRef.current.set(id, theme.heroImage);
        publicApi
          .get(`/gallery/images/homepage_carousel?siteId=${theme.id}`)
          .then((res) => {
            const imgs = res.data?.data || [];
            const first = imgs.find((i: any) => i.imge_url_tx)?.imge_url_tx;
            if (first && imageCacheRef.current.get(id) !== first) {
              imageCacheRef.current.set(id, first);
              if (hoveredRef.current === id) showPopupRef.current(id);
            }
          })
          .catch(() => {}); // keep themed fallback
      }
    }
    const photo = imageCacheRef.current.get(id);

    const chip = p.area_sqkm
      ? `<div class="uda-card__chip">
           <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0Z"/><circle cx="12" cy="10" r="3"/></svg>
           <span><b>${p.area_sqkm}</b> sq.km planning area</span>
         </div>`
      : '';
    const html =
      `<div class="uda-card">
         ${photo ? `<div class="uda-card__photo" style="background-image:url('${photo}')"></div>` : ''}
         <div class="uda-card__body">
           <div class="uda-card__head">
             <div class="uda-card__badge">
               <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18M5 21V7l7-4 7 4v14"/><path d="M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01"/></svg>
             </div>
             <div class="uda-card__titles">
               <div class="uda-card__name">${p.name}</div>
               <div class="uda-card__dist">${p.district} District</div>
             </div>
           </div>
           ${chip}
           <div class="uda-card__cta">Click to open portal<span>&rarr;</span></div>
         </div>
       </div>`;
    popupRef.current
      ?.setLngLat([p.lng, p.lat])
      .setHTML(html)
      .addTo(m);
  }, [publicApi]);
  showPopupRef.current = showPopup;

  const clearPopup = useCallback(() => { popupRef.current?.remove(); }, []);

  // ── Scroll the list so the active UDA row is visible (only if off-screen) ──
  const scrollListToUda = useCallback((id: number) => {
    const container = listScrollRef.current;
    const el = itemRefs.current.get(id);
    if (!container || !el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    if (eRect.top < cRect.top) {
      container.scrollBy({ top: eRect.top - cRect.top - 8, behavior: 'smooth' });
    } else if (eRect.bottom > cRect.bottom) {
      container.scrollBy({ top: eRect.bottom - cRect.bottom + 8, behavior: 'smooth' });
    }
  }, []);

  // ── Init map ──
  useEffect(() => {
    if (!mapContainer.current || map.current) return;
    const udaProps = udaPropsRef.current;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        // Glyphs are required for text labels (district names).
        glyphs: 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf',
        sources: {
          osm: {
            type: 'raster',
            tiles: [TILE_URLS.osmPlain],
            tileSize: 256,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          },
        },
        layers: [{ id: 'osm-tiles', type: 'raster', source: 'osm', minzoom: 0, maxzoom: 19, paint: { 'raster-opacity': 0.85 } }],
      },
      center: [80.5, 15.9],
      zoom: 6.4,
      attributionControl: false,
    });
    const m = map.current;
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    m.addControl(new maplibregl.AttributionControl({ compact: true }));

    popupRef.current = new maplibregl.Popup({ offset: 14, closeButton: false, closeOnClick: false, className: 'uda-popup' });

    m.on('load', async () => {
      try {
        const [state, districts, udas] = await Promise.all([
          fetch('/geo/ap-state.geojson').then(r => r.json()),
          fetch('/geo/ap-districts.geojson').then(r => r.json()),
          fetch('/geo/ap-udas.geojson').then(r => r.json()),
        ]);

        // Add API UDAs that do not have a polygon yet as point-only features.
        // They remain visible in the map/list instead of being silently dropped.
        const polygonSlugs = new Set((udas.features || []).map((f: any) => String(f.properties?.slug || '').toLowerCase()));
        const missingPointFeatures = udaList
          .filter((u) => !polygonSlugs.has(u.slug) && u.lat !== 0 && u.lng !== 0)
          .map((u) => ({
            type: 'Feature',
            id: u.id,
            properties: { id: u.id, slug: u.slug, name: u.name, district: u.district, lat: u.lat, lng: u.lng },
            geometry: { type: 'Point', coordinates: [u.lng, u.lat] },
          }));
        const allUdaFeatures = [...(udas.features || []), ...missingPointFeatures];

        // Cache UDA props + build a centroid (dot) source for every UDA.
        const dotFeatures = allUdaFeatures.map((f: any) => {
          udaPropsRef.current.set(f.properties.id, f.properties);
          return {
            type: 'Feature',
            id: f.properties.id,
            properties: f.properties,
            geometry: { type: 'Point', coordinates: [f.properties.lng, f.properties.lat] },
          };
        });

        // Districts — soft categorical choropleth: each district gets its own
        // pastel tint, separated by gray dashed lines (distinct from the solid
        // dark-orange UDA boundaries).
        districts.features.forEach((f: any, i: number) => {
          f.properties = f.properties || {};
          f.properties.__color = DISTRICT_PALETTE[i % DISTRICT_PALETTE.length];
        });
        m.addSource('districts', { type: 'geojson', data: districts });
        m.addLayer({
          id: 'district-fill', type: 'fill', source: 'districts',
          paint: { 'fill-color': ['get', '__color'], 'fill-opacity': 0.38 },
        });
        m.addLayer({
          id: 'district-line', type: 'line', source: 'districts',
          paint: { 'line-color': '#6b7280', 'line-width': 1, 'line-opacity': 0.6, 'line-dasharray': [3, 2] },
        });

        // State outline
        m.addSource('state', { type: 'geojson', data: state });
        m.addLayer({ id: 'state-line', type: 'line', source: 'state', paint: { 'line-color': ACCENT_DARK, 'line-width': 1.6, 'line-opacity': 0.7 } });

        // UDA boundaries (polygons), highlighted on hover via feature-state
        m.addSource('udas', { type: 'geojson', data: { type: 'FeatureCollection', features: allUdaFeatures }, promoteId: 'id' });
        m.addLayer({
          id: 'uda-fill', type: 'fill', source: 'udas',
          paint: { 'fill-color': ACCENT, 'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.5, 0.22] },
        });
        m.addLayer({
          id: 'uda-line', type: 'line', source: 'udas',
          paint: {
            'line-color': ACCENT_DARK,
            'line-width': ['case', ['boolean', ['feature-state', 'hover'], false], 3.5, 2],
            'line-opacity': 0.9,
          },
        });

        // UDA centre dots (always visible, grow on hover)
        m.addSource('uda-dots', { type: 'geojson', data: { type: 'FeatureCollection', features: dotFeatures }, promoteId: 'id' });
        m.addLayer({
          id: 'uda-dot', type: 'circle', source: 'uda-dots',
          paint: {
            'circle-radius': ['case', ['boolean', ['feature-state', 'hover'], false], 9, 5.5],
            'circle-color': ACCENT,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': ['case', ['boolean', ['feature-state', 'hover'], false], 3, 2],
            'circle-opacity': 1,
          },
        });

        // District name labels (placed at each district centroid). Overlapping
        // labels are auto-hidden so the map stays readable at AP-wide zoom.
        const labelFeatures = districts.features
          .filter((f: any) => Array.isArray(f.properties?.centroid))
          .map((f: any) => ({
            type: 'Feature',
            properties: { name: f.properties.name },
            geometry: { type: 'Point', coordinates: f.properties.centroid },
          }));
        m.addSource('district-labels', { type: 'geojson', data: { type: 'FeatureCollection', features: labelFeatures } });
        m.addLayer({
          id: 'district-label', type: 'symbol', source: 'district-labels',
          layout: {
            'text-field': ['get', 'name'],
            'text-font': ['Open Sans Semibold', 'Noto Sans Regular'],
            'text-size': 11,
            'text-allow-overlap': false,
            'text-padding': 3,
            'text-max-width': 8,
          },
          paint: {
            'text-color': '#44403c',
            'text-halo-color': 'rgba(255,255,255,0.95)',
            'text-halo-width': 1.5,
          },
        });

        // Interactions on map (hover dots + polygons). The dot layer sits on
        // top of the fill, so crossing between them fires leave+enter — we
        // defer the leave briefly so an immediate re-enter cancels it.
        let leaveTimer: ReturnType<typeof setTimeout> | null = null;
        const onEnter = (e: maplibregl.MapLayerMouseEvent) => {
          const f = e.features?.[0];
          if (!f) return;
          if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
          const id = Number(f.id);
          m.getCanvas().style.cursor = 'pointer';
          // Bound to mousemove too — bail if we're still on the same UDA so the
          // popup isn't torn down and re-rendered (which caused the blinking).
          if (hoveredRef.current === id) return;
          setActiveUdaId(id);
          setMapHover(id);
          showPopup(id);
          scrollListToUda(id);
        };
        const onLeave = () => {
          if (leaveTimer) clearTimeout(leaveTimer);
          leaveTimer = setTimeout(() => {
            leaveTimer = null;
            if (!map.current) return;
            m.getCanvas().style.cursor = '';
            setMapHover(null);
            clearPopup();
          }, 80);
        };
        const onClick = (e: maplibregl.MapLayerMouseEvent) => {
          const f = e.features?.[0];
          if (!f) return;
          const p = udaPropsRef.current.get(Number(f.id));
          if (p) window.open(buildUdaUrl({ slug: p.slug }), '_blank', 'noopener,noreferrer');
        };
        ['uda-dot', 'uda-fill'].forEach(layer => {
          m.on('mouseenter', layer, onEnter);
          m.on('mousemove', layer, onEnter);
          m.on('mouseleave', layer, onLeave);
          m.on('click', layer, onClick);
        });

        // Fit the view tightly to Andhra Pradesh and stop the map from
        // panning/zooming out to the rest of India.
        try {
          const bounds = new maplibregl.LngLatBounds();
          const extend = (coords: any) => {
            if (typeof coords[0] === 'number') bounds.extend(coords as [number, number]);
            else (coords as any[]).forEach(extend);
          };
          state.features.forEach((f: any) => extend(f.geometry.coordinates));
          m.fitBounds(bounds, { padding: 24, duration: 0 });
          m.setMinZoom(m.getZoom() - 0.4);
          const sw = bounds.getSouthWest();
          const ne = bounds.getNorthEast();
          const pad = 1.2; // degrees of slack so the edges aren't clipped
          m.setMaxBounds([
            [sw.lng - pad, sw.lat - pad],
            [ne.lng + pad, ne.lat + pad],
          ]);
        } catch (e) {
          console.warn('[ModulesSection] could not fit AP bounds:', e);
        }

        setMapLoaded(true);
      } catch (err) {
        console.warn('[ModulesSection] GeoJSON layers unavailable:', err);
      }
    });

    return () => {
      popupRef.current?.remove();
      m.remove();
      map.current = null;
      udaProps.clear();
    };
  }, [buildUdaUrl, setMapHover, showPopup, clearPopup, scrollListToUda, udaList]);

  // ── Hover a list row → highlight + show data on the map ──
  const handleListHover = useCallback((uda: UDA) => {
    setActiveUdaId(uda.id);
    if (!mapLoaded) return;
    setMapHover(uda.id);
    showPopup(uda.id);
  }, [mapLoaded, setMapHover, showPopup]);

  const handleListLeave = useCallback(() => {
    setMapHover(null);
    clearPopup();
  }, [setMapHover, clearPopup]);

  // ── Preview (zoom) a UDA on the map ──
  const previewUdaOnMap = useCallback((uda: UDA) => {
    setActiveUdaId(uda.id);
    const p = udaPropsRef.current.get(uda.id);
    const lngLat: [number, number] = p ? [p.lng, p.lat] : [uda.lng, uda.lat];
    map.current?.flyTo({ center: lngLat, zoom: 10.5, duration: 1000 });
    if (mapLoaded) { setMapHover(uda.id); showPopup(uda.id); }
  }, [mapLoaded, setMapHover, showPopup]);

  const openUdaInNewTab = useCallback((uda: UDA) => {
    setActiveUdaId(uda.id);
    // Always open the UDA on its own subdomain in a new tab — never mutate the
    // current page (no ?uda= query state).
    window.open(buildUdaUrl(uda), '_blank', 'noopener,noreferrer');
  }, [buildUdaUrl]);

  return (
    <section id="modules" className="portal-section portal-surface-alt portal-rule scroll-mt-16">
      <div className="portal-container">
        <SectionHeading
          eyebrow="Jurisdictions"
          title={t('uda.title')}
          subtitle={t('uda.subtitle', { count: udaList.length })}
          action={
            <span
              className="hidden md:inline-flex items-center gap-1.5 text-[13px] font-bold text-[color:var(--portal-accent-dark)] bg-[var(--portal-accent-soft)] dark:bg-white/[0.08] dark:text-[color:var(--portal-accent-light)] px-4 py-2 rounded-full"
              style={{ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--portal-accent) 25%, transparent)' }}
            >
              <i className="ri-map-pin-2-line text-sm" />
              {t('uda.badge', { count: udaList.length })}
            </span>
          }
        />

        {/* Map + UDA List Layout */}
        <div
          ref={contentRef as React.RefObject<HTMLDivElement>}
          className={`grid grid-cols-1 lg:grid-cols-5 gap-5 scroll-animate ${contentVisible ? 'visible' : ''}`}
        >
          {/* Left: Map */}
          <div className="lg:col-span-3 rounded-3xl overflow-hidden shadow-[0_16px_40px_-18px_rgba(0,0,0,0.35)] border border-black/[0.07] dark:border-white/10 bg-white dark:bg-white/[0.04] relative">
            <div ref={mapContainer} className="w-full h-[400px] lg:h-[500px]" />
            {/* Legend. Stays light in both themes — it floats on the OSM raster
                tiles, which are always light, so a dark panel here would be a
                dark box on a light map rather than a legend.
                NOTE: `bg-white/95`, not `/92` — 92 is not a step on Tailwind's
                opacity scale, so `bg-white/92` compiled to nothing at all and
                the legend had been sitting directly on the map with no panel
                behind it. */}
            <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-md rounded-xl shadow-[0_6px_18px_-8px_rgba(0,0,0,0.35)] border border-black/[0.06] px-3.5 py-2.5 text-[11px] text-gray-600 flex flex-col gap-1.5 pointer-events-none">
              <div className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full border-2 border-white shadow"
                  style={{ background: 'var(--portal-accent)' }}
                />
                Urban Development Authority
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="w-3 h-2 rounded-sm"
                  style={{
                    border: '1px solid var(--portal-accent-dark)',
                    background: 'color-mix(in srgb, var(--portal-accent) 20%, transparent)',
                  }}
                />
                UDA boundary
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 border-t-2 border-dashed border-gray-400" />
                District boundary
              </div>
            </div>
          </div>

          {/* Right: UDA List */}
          <div className="lg:col-span-2 bg-white dark:bg-white/[0.04] rounded-3xl shadow-[0_16px_40px_-18px_rgba(0,0,0,0.35)] border border-black/[0.07] dark:border-white/10 flex flex-col overflow-hidden">
            <div className="px-5 py-4 border-b border-black/[0.06] dark:border-white/10 flex items-center gap-2.5">
              {/* The accent tints are classes, not inline styles, so they can
                  carry a dark pair — `--portal-accent-soft` is a cream and read
                  as a bright blob on the dark card. */}
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[var(--portal-accent-soft)] dark:bg-white/[0.08]">
                <i className="ri-building-2-line text-base text-[color:var(--portal-accent)] dark:text-[color:var(--portal-accent-light)]"></i>
              </div>
              <h3 className="font-bold text-[#161A22] dark:text-white text-[15px]">{t('uda.listTitle')}</h3>
              <span className="ml-auto text-[11px] font-bold px-2.5 py-1 rounded-full bg-[var(--portal-accent-soft)] text-[color:var(--portal-accent-dark)] dark:bg-white/[0.08] dark:text-[color:var(--portal-accent-light)]">
                {udaList.length}
              </span>
            </div>
            {/* Search */}
            <div className="px-4 py-3 border-b border-black/[0.06] dark:border-white/10">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  placeholder={t('uda.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-black/[0.08] dark:border-white/10 rounded-full bg-[#F6F5F3] dark:bg-white/[0.06] dark:text-white focus:bg-white dark:focus:bg-white/[0.1] focus:ring-2 focus:ring-[color:var(--portal-accent)] focus:border-[color:var(--portal-accent)] outline-none transition-all duration-200 placeholder:text-gray-400 dark:placeholder:text-white/40"
                />
              </div>
            </div>
            <div ref={listScrollRef} className="overflow-y-auto flex-1 max-h-[300px] lg:max-h-[390px] p-2" onMouseLeave={handleListLeave}>
              {udaList.filter((uda) => {
                if (!searchTerm) return true;
                const q = searchTerm.toLowerCase();
                return uda.name.toLowerCase().includes(q) || uda.district.toLowerCase().includes(q);
              }).map((uda, index) => (
                <a
                  key={uda.id}
                  ref={(el) => {
                    if (el) itemRefs.current.set(uda.id, el);
                    else itemRefs.current.delete(uda.id);
                  }}
                  href={buildUdaUrl(uda)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={() => handleListHover(uda)}
                  onClick={(e) => {
                    e.preventDefault();
                    openUdaInNewTab(uda);
                  }}
                  title={`Open ${uda.name} details in a new tab`}
                  style={
                    activeUdaId === uda.id
                      ? { borderLeftColor: 'var(--portal-accent)' }
                      : undefined
                  }
                  className={`group w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl mb-0.5 border-l-4 transition-all duration-200 ease-in-out ${
                    activeUdaId === uda.id
                      ? 'shadow-sm bg-[var(--portal-accent-soft)] dark:bg-white/[0.08]'
                      : 'border-l-transparent hover:bg-black/[0.03] dark:hover:bg-white/[0.06] hover:shadow-sm hover:-translate-y-[1px]'
                  }`}
                >
                  <div
                    style={
                      activeUdaId === uda.id
                        ? { background: 'var(--portal-accent)', color: '#fff' }
                        : undefined
                    }
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold transition-all duration-200 ${
                      activeUdaId === uda.id
                        ? 'shadow-[0_4px_12px_-3px_rgba(0,0,0,0.4)]'
                        : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/70'
                    }`}
                  >
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`font-semibold text-sm truncate transition-colors duration-200 ${
                        activeUdaId === uda.id
                          ? 'text-[color:var(--portal-accent-dark)] dark:text-[color:var(--portal-accent-light)]'
                          : 'text-gray-800 dark:text-white/85'
                      }`}
                    >
                      {uda.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-white/50 truncate">{uda.district} {t('uda.district')}</div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); previewUdaOnMap(uda); }}
                    title="Zoom to this UDA on the map"
                    className="p-1.5 rounded-lg text-gray-400 dark:text-white/45 hover:text-[color:var(--portal-accent-dark)] hover:bg-[var(--portal-accent-soft)] dark:hover:text-[color:var(--portal-accent-light)] dark:hover:bg-white/[0.08] transition-colors"
                  >
                    <i className="ri-map-pin-line text-sm" />
                  </button>
                  <i
                    style={activeUdaId === uda.id ? { color: 'var(--portal-accent)' } : undefined}
                    className={`ri-external-link-line text-sm flex-shrink-0 transition-all duration-200 ${
                      activeUdaId === uda.id ? '' : 'text-gray-400 dark:text-white/45 group-hover:scale-110'
                    }`}
                  ></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
